import type { Plugin } from 'unified'
import type { PhrasingContent, Root } from 'mdast'
import { findAndReplace } from 'mdast-util-find-and-replace'
import { buildGlossaryRegex, lookupGlossaryEntry } from './glossary.js'

/**
 * remark plugin (frontend task #61): decorate the FIRST mention of each curated glossary term in the chat
 * OVERVIEW digest with an inline info affordance whose tooltip carries the definition (see {@link ./glossary}
 * for the terms + {@link GlossaryTerm} for the rendering). Mirrors {@link ./remarkCiteRefs}: it runs on the
 * mdast tree AFTER remark-gfm via {@link findAndReplace}, so it only ever touches `Text` nodes — terms inside
 * code spans / links are `InlineCode.value` / node structure, not `Text`, and are left alone.
 *
 * FIRST-OCCURRENCE ONLY: a `seen` set keyed by the glossary CONCEPT id (not the raw alias) is closed over the
 * single findAndReplace pass. findAndReplace visits text nodes in document order, so the first mention of any
 * of a concept's aliases anywhere in the digest is decorated and every later mention is left as plain text —
 * returning `false` from the replace fn tells findAndReplace to keep the original text untouched. This keeps
 * the digest reading clean rather than peppering every occurrence.
 *
 * The matched text is emitted as a custom `glossaryTerm` node with `data.hName = 'gterm'`; mdast-util-to-hast
 * honors that and emits `<gterm gid=… label=… def=…>original text</gterm>`, which react-markdown maps to the
 * {@link GlossaryTerm} component via its `components` prop. The definition is carried as an attribute so the
 * renderer needs no glossary lookup of its own. The regex matches case-insensitively on word boundaries, so
 * this works regardless of the exact digest wording.
 */
export function remarkGlossary(): Plugin<[], Root> {
  return () => (tree: Root): undefined => {
    const seen = new Set<string>()
    findAndReplace(tree, [
      buildGlossaryRegex(),
      (matched: string): PhrasingContent | string | false => {
        const entry = lookupGlossaryEntry(matched)
        if (!entry) return false // unknown surface form — leave as-is (defensive; regex only matches aliases)
        if (seen.has(entry.id)) return false // not the first mention of this concept — keep plain text
        seen.add(entry.id)
        // Custom mdast node: no known handler, so mdast-util-to-hast honors `data.hName`/`hProperties` and
        // renders the original text (via `children`) inside the carrier element. Cast because this shape is
        // intentionally outside the mdast union (same trick as remarkCiteRefs).
        const node = {
          type: 'glossaryTerm',
          data: {
            hName: 'gterm',
            hProperties: { gid: entry.id, label: entry.label, def: entry.definition }
          },
          children: [{ type: 'text', value: matched }]
        }
        return node as unknown as PhrasingContent
      }
    ])
    return undefined
  }
}
